import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import type {
    CapabilityAccessAction,
    CapabilityAccessBlocker,
    CapabilityAccessDecision,
    CapabilityAccessPresentationState,
    CapabilityAccessView
} from "../types/capabilityAccess";
import {
    AGREEMENT_STAGE_LABEL,
    capabilityAccessActionLabel,
    isCapabilityGrantAccessAction
} from "../types/capabilityAccess";

import { Button } from "./ui/button";

const STATE_LABEL: Record<CapabilityAccessPresentationState, string> = {
    STATUS_UNAVAILABLE: "Status unavailable",
    ACCESS_REMOVED: "Access removed",
    NOT_AVAILABLE: "Not available",
    PARTIAL_ACCESS: "Partial access",
    MIXED_STATUS: "Mixed status",
    SUSPENDED: "Suspended",
    ACTION_REQUIRED: "Action required",
    AGREEMENT_PENDING: "Agreement pending",
    VERIFICATION_PENDING: "Verification pending",
    PENDING_ACTIVATION: "Pending activation",
    ENABLED: "Enabled",
    ENABLED_UPDATE_REQUIRED: "Enabled, update required",
    BLOCKED: "Blocked"
};

const BLOCKER_COPY: Record<CapabilityAccessBlocker, string> = {
    CAPABILITY_NOT_GRANTED: "The distributor has not granted this capability.",
    CAPABILITY_GRANT_SUSPENDED: "The distributor suspended this capability.",
    CAPABILITY_GRANT_REVOKED: "The distributor removed this capability.",
    ISSUER_CLIENT_PENDING: "The relationship is waiting for activation.",
    ISSUER_CLIENT_SUSPENDED: "The relationship is suspended.",
    ISSUER_CLIENT_CANCELLED: "The relationship is cancelled.",
    ISSUER_CAPABILITY_REQUIRED:
        "The distributor cannot provide this capability now.",
    ISSUER_CAPABILITY_SUSPENDED: "The distributor capability is suspended.",
    CAPABILITY_REQUIREMENT_INCOMPLETE:
        "A required agreement step is incomplete.",
    CAPABILITY_REQUIREMENT_VERSION_NOT_EFFECTIVE:
        "The linked agreement version is not active.",
    AGREEMENT_NOT_STARTED: "The Mint and Redeem Agreement has not started.",
    AGREEMENT_IN_PROGRESS: "The Mint and Redeem Agreement is in progress.",
    AGREEMENT_VERIFICATION_PENDING:
        "YaaS is verifying the completed agreement.",
    AGREEMENT_EVIDENCE_INELIGIBLE:
        "The agreement evidence cannot satisfy this requirement.",
    AGREEMENT_ARTIFACT_INVALID: "The agreement package is invalid.",
    AGREEMENT_ARTIFACT_NOT_ALLOWED_IN_ENVIRONMENT:
        "The agreement package is not allowed in this environment.",
    CAPABILITY_ACCESS_EVALUATION_FAILED:
        "YaaS could not confirm current access."
};

function blockerCopy(
    blocker: CapabilityAccessBlocker,
    audience: "issuer" | "client"
): string {
    if (blocker !== "AGREEMENT_EVIDENCE_INELIGIBLE")
        return BLOCKER_COPY[blocker];
    return audience === "client"
        ? "The distributor is reviewing this agreement evidence. Contact the distributor if you need an update."
        : "This Verified agreement evidence is ineligible. Reinstate it only after remediation.";
}

export interface CapabilityAccessStatusProps {
    access: CapabilityAccessView | null;
    error: Error | null;
    isLoading: boolean;
    audience: "issuer" | "client";
    onAction?: (action: CapabilityAccessAction) => void;
    grantPending?: boolean;
    onDocument?: (kind: "signed-document" | "completion-certificate") => void;
    /** Use h3 when the capability sits inside a broader access section. */
    headingLevel?: "h2" | "h3";
    /**
     * Which half to render. `access` is the authorization: the pair's state,
     * the Mint and Redeem results, what blocks them, and the grant actions.
     * `agreement` is the Mint and Redeem Agreement: its progress and version,
     * future requirements and the signed documents.
     * `all` (the default) is both, in one section.
     */
    part?: "all" | "access" | "agreement";
}

function decisionText(decision: CapabilityAccessDecision): string {
    if (decision.allowed) return "Enabled";
    if (decision.grantState === "Revoked") return "Access removed";
    if (decision.grantState === "Suspended") return "Suspended";
    return "Not enabled";
}

function hasDifferentResults(access: CapabilityAccessView): boolean {
    return (
        access.mint.allowed !== access.redeem.allowed ||
        access.mint.grantState !== access.redeem.grantState ||
        access.mint.blockers.join("|") !== access.redeem.blockers.join("|")
    );
}

function formatEvaluationTime(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-US");
}

export function CapabilityAccessStatus({
    access,
    error,
    isLoading,
    audience,
    onAction,
    grantPending = false,
    onDocument,
    headingLevel = "h2",
    part = "all"
}: CapabilityAccessStatusProps): React.ReactElement {
    const Heading = headingLevel;
    const showAccess = part !== "agreement";
    const showAgreement = part !== "access";
    const serverUnavailable =
        access?.presentationState === "STATUS_UNAVAILABLE";
    const unavailable = Boolean(error) || serverUnavailable;
    const state = unavailable
        ? "STATUS_UNAVAILABLE"
        : (access?.presentationState ?? "STATUS_UNAVAILABLE");
    const blockers = unavailable
        ? (["CAPABILITY_ACCESS_EVALUATION_FAILED"] as CapabilityAccessBlocker[])
        : [
              ...new Set([
                  ...(access?.mint.blockers ?? []),
                  ...(access?.redeem.blockers ?? [])
              ])
          ];
    const showCapabilityRows = Boolean(
        !unavailable &&
            access &&
            (audience === "issuer" || hasDifferentResults(access))
    );
    const agreementStatus = unavailable
        ? "Agreement status is unavailable."
        : !access
          ? isLoading
              ? "Checking agreement status."
              : "Agreement status is unavailable."
          : access.requirements.length
            ? `Progress: ${AGREEMENT_STAGE_LABEL[access.agreement.stage]}`
            : "Not required until the distributor grants access.";
    const pairNeedsAttention =
        state === "PARTIAL_ACCESS" || state === "MIXED_STATUS";
    const actions = unavailable
        ? []
        : (access?.allowedActions ?? []).filter(
              (action) => showAccess && isCapabilityGrantAccessAction(action)
          );
    const showIndependentClientEvidenceStatus =
        audience === "client" &&
        access?.agreement.evidenceEligibility?.status === "Ineligible" &&
        !blockers.includes("AGREEMENT_EVIDENCE_INELIGIBLE");

    return (
        <section
            aria-labelledby={
                showAccess
                    ? "mint-redeem-access-heading"
                    : "mint-redeem-agreement-heading"
            }
            className="border-b border-line-base px-6 py-6"
        >
            {showAccess ? (
                <>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <Heading
                                id="mint-redeem-access-heading"
                                className="text-base font-semibold text-foreground-base"
                            >
                                Mint and Redeem Access
                            </Heading>
                            <p className="mt-1 text-sm text-foreground-muted">
                                Mint and Redeem have separate authorization
                                results for this relationship.
                            </p>
                        </div>
                        <p
                            role="status"
                            className="text-sm font-medium text-foreground-base"
                        >
                            {isLoading && !access
                                ? "Checking status"
                                : pairNeedsAttention
                                  ? `Needs attention: ${STATE_LABEL[state]}`
                                  : STATE_LABEL[state]}
                        </p>
                    </div>

                    {access?.evaluatedAt && !serverUnavailable ? (
                        <p className="mt-2 text-xs text-foreground-muted">
                            {unavailable ? "Last confirmed" : "Evaluated"}:{" "}
                            {formatEvaluationTime(access.evaluatedAt)}
                        </p>
                    ) : null}

                    {access && !access.rolloutAvailable ? (
                        <Text
                            variant="small"
                            render={<p />}
                            role="status"
                            aria-label="Release availability"
                            className="mt-3 text-foreground-muted"
                        >
                            New Mint and Redeem access work is unavailable.
                            Existing access and safety actions remain available.
                        </Text>
                    ) : null}

                    {showCapabilityRows && access ? (
                        <dl className="mt-4 grid gap-2 sm:grid-cols-2">
                            <CapabilityRow
                                label="Mint"
                                decision={access.mint}
                            />
                            <CapabilityRow
                                label="Redeem"
                                decision={access.redeem}
                            />
                        </dl>
                    ) : null}
                </>
            ) : null}

            {showAgreement ? (
                <>
                    <div className={showAccess ? "mt-4" : undefined}>
                        <p
                            id="mint-redeem-agreement-heading"
                            className="text-sm font-medium text-foreground-base"
                        >
                            Mint and Redeem Agreement
                        </p>
                        <p className="mt-1 text-sm text-foreground-muted">
                            {agreementStatus}
                        </p>
                        {access?.requirements.length &&
                        access.agreement.version ? (
                            <Text
                                variant="fine-print"
                                className="mt-1 text-foreground-muted"
                            >
                                Version {access.agreement.version}
                                {access.agreement.documentVersion
                                    ? ` · ${access.agreement.documentVersion}`
                                    : ""}
                                {access.agreement.useClass
                                    ? ` · ${access.agreement.useClass === "Test" ? "Test use only" : "Production use"}`
                                    : ""}
                            </Text>
                        ) : null}
                    </div>

                    {showIndependentClientEvidenceStatus ? (
                        <p
                            role="status"
                            className="mt-4 text-sm text-foreground-muted"
                        >
                            The distributor is reviewing this agreement
                            evidence. Contact the distributor if you need an
                            update.
                        </p>
                    ) : null}

                    {!unavailable && access?.futureRequirements?.length ? (
                        <div className="mt-4 border border-line-base px-3 py-3">
                            <p className="text-sm font-medium text-foreground-base">
                                Future agreement requirement
                            </p>
                            {access.futureRequirements.map((requirement) => (
                                <div key={requirement.uuid}>
                                    <p className="mt-1 text-sm text-foreground-muted">
                                        Version {requirement.moduleVersion}{" "}
                                        becomes required on{" "}
                                        {formatEvaluationTime(
                                            requirement.effectiveFrom
                                        )}
                                        .
                                        {requirement.incomplete
                                            ? " Complete this version before the cutoff to keep access enabled."
                                            : " This version is complete."}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </>
            ) : null}

            {showAccess && blockers.length ? (
                <ul className="mt-4 space-y-1 text-sm text-foreground-muted">
                    {blockers.map((blocker) => (
                        <li key={blocker}>{blockerCopy(blocker, audience)}</li>
                    ))}
                </ul>
            ) : null}

            {actions.length ? (
                <div className="mt-4 flex flex-wrap gap-2">
                    {actions.map((action) => (
                        <Button
                            key={action}
                            type="button"
                            size="sm"
                            onClick={() => onAction?.(action)}
                            disabled={grantPending || !onAction}
                        >
                            {capabilityAccessActionLabel(
                                action,
                                pairNeedsAttention
                            )}
                        </Button>
                    ))}
                </div>
            ) : null}
            {showAgreement &&
            !unavailable &&
            access?.agreement.documentKinds?.length ? (
                <div className="mt-4 flex flex-wrap gap-2">
                    {access.agreement.documentKinds.map((kind) => (
                        <Button
                            key={kind}
                            type="button"
                            size="sm"
                            variant="sunken"
                            onClick={() => onDocument?.(kind)}
                            disabled={!onDocument}
                        >
                            {kind === "signed-document"
                                ? "Download signed agreement"
                                : "Download completion certificate"}
                        </Button>
                    ))}
                </div>
            ) : null}
        </section>
    );
}

function CapabilityRow({
    label,
    decision
}: {
    label: string;
    decision: CapabilityAccessDecision;
}): React.ReactElement {
    return (
        <div className="border border-line-base px-3 py-2">
            <dt className="text-xs text-foreground-muted">{label}</dt>
            <dd className="mt-1 text-sm font-medium text-foreground-base">
                {decisionText(decision)}
            </dd>
        </div>
    );
}
